import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Loader2, X, Plus, Tag as TagIcon } from 'lucide-react';
import { toast } from 'sonner';
import { productService, QUERY_KEYS } from '@/services/productService';
import { useQueryClient } from '@tanstack/react-query';
import AsyncCreatableSelect from 'react-select/async-creatable';
import { OnChangeValue } from 'react-select';
import { useProductDetailPageTranslations } from '@/hooks/useProductDetailPageTranslations';
import { logError } from '@/utils/errorHandler';

interface TagOption {
  label: string;
  value: string;
}

interface ProductTagsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  productId: number;
  currentTags: string[];
  onTagsUpdated: () => Promise<void>;
}

export function ProductTagsModal({
  open,
  onOpenChange,
  productId,
  currentTags,
  onTagsUpdated,
}: ProductTagsModalProps) {
  const queryClient = useQueryClient();

  // Local state for managing tags
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Get translations
  const { getTagsModalLabels, getTagsModalNotification, getProductComponentsText } =
    useProductDetailPageTranslations();

  // Initialize selected tags when modal opens
  useEffect(() => {
    if (open) {
      setSelectedTags([...currentTags]);
    }
  }, [open, currentTags]);

  // Load tag options with search - optimized with better caching
  const loadTagOptions = async (inputValue: string): Promise<TagOption[]> => {
    try {
      // Pass empty string to get all tags when no input is provided
      const searchQuery = inputValue ? inputValue.trim() : '';
      const searchResults = await productService.searchTags(searchQuery);

      // Filter out already selected tags from the results
      return searchResults.filter(tag => !selectedTags.includes(tag.value));
    } catch (error) {
      logError(
        error instanceof Error ? error : 'Failed to search tags',
        {
          component: 'ProductTagsModal',
          action: 'searchTags',
          metadata: {
            searchQuery: inputValue ? inputValue.trim() : '',
          },
        },
        {
          showToast: false, // Don't show toast for search failures
          captureInSentry: true,
          userFacing: false,
        }
      );
      return [];
    }
  };

  // Handle creating new tags
  const handleCreateTag = async (inputValue: string): Promise<TagOption | null> => {
    if (!inputValue.trim()) return null;

    try {
      const newTag = await productService.createTag({ name: inputValue.trim() });
      const newOption: TagOption = {
        label: newTag.name,
        value: newTag.name,
      };

      // Show success notification with the tag name
      toast.success(getTagsModalNotification('tagCreated', { name: newTag.name }));
      return newOption;
    } catch (error) {
      logError(
        error instanceof Error ? error : 'Failed to create tag',
        {
          component: 'ProductTagsModal',
          action: 'createTag',
          metadata: {
            tagName: inputValue.trim(),
          },
        },
        {
          showToast: false, // User already gets toast from existing code
          captureInSentry: true,
          userFacing: false,
        }
      );
      toast.error(getTagsModalNotification('createFailed'));
      return null;
    }
  };

  // Handle adding tags via dropdown
  const handleTagSelect = (newValue: OnChangeValue<TagOption, true>) => {
    const selectedOptions = Array.isArray(newValue) ? newValue : [];
    const newTags = selectedOptions.map(option => option.value);

    // Merge with existing tags, avoiding duplicates
    const updatedTags = [...new Set([...selectedTags, ...newTags])];
    setSelectedTags(updatedTags);
  };

  // Handle removing individual tags
  const handleRemoveTag = (tagToRemove: string) => {
    setSelectedTags(prev => prev.filter(tag => tag !== tagToRemove));
  };

  // Handle saving changes
  const handleSave = async () => {
    setIsSubmitting(true);
    try {
      // Update product with new tags
      await productService.updateProduct(productId, { tags: selectedTags });

      // Invalidate queries to refresh UI
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PRODUCT(productId) });

      // Call the callback to refresh parent component
      await onTagsUpdated();

      toast.success(getTagsModalNotification('tagsUpdated'));
      onOpenChange(false);
    } catch (error) {
      logError(
        error instanceof Error ? error : 'Failed to update product tags',
        {
          component: 'ProductTagsModal',
          action: 'updateProductTags',
          metadata: {
            productId,
            selectedTags,
            previousTags: currentTags,
          },
        },
        {
          showToast: false, // User already gets toast from existing code
          captureInSentry: true,
          userFacing: false,
        }
      );
      toast.error(getTagsModalNotification('updateFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    setSelectedTags([...currentTags]);
    onOpenChange(false);
  };

  // Check if there are changes
  const hasChanges = useMemo(() => {
    if (selectedTags.length !== currentTags.length) return true;
    return (
      selectedTags.some(tag => !currentTags.includes(tag)) ||
      currentTags.some(tag => !selectedTags.includes(tag))
    );
  }, [selectedTags, currentTags]);

  // Add CSS to ensure React Select works properly in dialogs
  useEffect(() => {
    const style = document.createElement('style');
    style.id = 'product-tags-modal-styles';
    style.textContent = `
      .react-select__menu {
        z-index: 9999 !important;
        position: absolute !important;
      }
      .react-select__menu-list {
        max-height: 200px !important;
      }
      [data-radix-dialog-content] {
        overflow: visible !important;
      }
      [data-radix-dialog-content] .react-select__menu {
        z-index: 9999 !important;
        position: absolute !important;
      }
    `;

    if (open) {
      document.head.appendChild(style);
    }

    return () => {
      const existingStyle = document.getElementById('product-tags-modal-styles');
      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-visible sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TagIcon className="size-5" />
            {getTagsModalLabels.title}
          </DialogTitle>
          <DialogDescription>{getTagsModalLabels.description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Current Tags Display */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">
              {getTagsModalLabels.labels.selectedTags} ({selectedTags.length})
            </Label>
            <div className="min-h-[60px] rounded-md border border-border bg-muted/30 p-3 dark:bg-muted/20">
              {selectedTags.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {selectedTags.map((tag, index) => (
                    <Badge
                      key={index}
                      variant="secondary"
                      className="flex items-center gap-1 border-primary/30 bg-primary/10 px-2 py-1 text-primary dark:bg-primary/20 dark:text-primary"
                    >
                      {tag}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="size-4 p-0 hover:bg-primary/30 dark:hover:bg-primary/40"
                        onClick={() => handleRemoveTag(tag)}
                        disabled={isSubmitting}
                        aria-label={`Remove tag ${tag}`}
                      >
                        <X className="size-3" />
                      </Button>
                    </Badge>
                  ))}
                </div>
              ) : (
                <div className="flex h-12 items-center justify-center text-muted-foreground">
                  <TagIcon className="mr-2 size-4" />
                  {getTagsModalLabels.states.noTags}
                </div>
              )}
            </div>
          </div>

          {/* Add Tags Section */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">{getTagsModalLabels.labels.addTags}</Label>
            <AsyncCreatableSelect<TagOption, true>
              isMulti
              cacheOptions
              defaultOptions={true} // Load default options when dropdown opens
              loadOptions={loadTagOptions}
              onCreateOption={async inputValue => {
                const newOption = await handleCreateTag(inputValue);
                if (newOption) {
                  setSelectedTags(prev => [...new Set([...prev, newOption.value])]);
                }
              }}
              onChange={handleTagSelect}
              value={[]} // Always empty since we manage tags separately
              placeholder={getTagsModalLabels.placeholders.searchTags}
              noOptionsMessage={({ inputValue }) =>
                inputValue
                  ? `No tags found for "${inputValue}"`
                  : 'Start typing to search or create tags'
              }
              formatCreateLabel={(inputValue: string) => `Create tag "${inputValue}"`}
              isDisabled={isSubmitting}
              isClearable
              closeMenuOnSelect={false}
              className="react-select-container"
              classNamePrefix="react-select"
              styles={{
                control: (base, state) => ({
                  ...base,
                  minHeight: 40,
                  backgroundColor: document.documentElement.classList.contains('dark')
                    ? '#1f2937'
                    : base.backgroundColor,
                  borderColor: state.isFocused
                    ? '#3b82f6'
                    : document.documentElement.classList.contains('dark')
                      ? '#374151'
                      : base.borderColor,
                  color: document.documentElement.classList.contains('dark')
                    ? '#f9fafb'
                    : base.color,
                  '&:hover': {
                    borderColor: '#3b82f6',
                  },
                }),
                menu: base => ({
                  ...base,
                  backgroundColor: document.documentElement.classList.contains('dark')
                    ? '#1f2937'
                    : base.backgroundColor,
                  border: document.documentElement.classList.contains('dark')
                    ? '1px solid #374151'
                    : base.border,
                  zIndex: 9999,
                  position: 'absolute',
                }),
                menuList: base => ({
                  ...base,
                  backgroundColor: document.documentElement.classList.contains('dark')
                    ? '#1f2937'
                    : base.backgroundColor,
                  maxHeight: 200,
                }),
                option: (base, state) => ({
                  ...base,
                  backgroundColor: state.isFocused
                    ? document.documentElement.classList.contains('dark')
                      ? '#374151'
                      : '#f3f4f6'
                    : document.documentElement.classList.contains('dark')
                      ? '#1f2937'
                      : base.backgroundColor,
                  color: document.documentElement.classList.contains('dark')
                    ? '#f9fafb'
                    : base.color,
                  '&:hover': {
                    backgroundColor: document.documentElement.classList.contains('dark')
                      ? '#374151'
                      : '#f3f4f6',
                  },
                }),
                placeholder: base => ({
                  ...base,
                  color: document.documentElement.classList.contains('dark')
                    ? '#9ca3af'
                    : base.color,
                }),
                singleValue: base => ({
                  ...base,
                  color: document.documentElement.classList.contains('dark')
                    ? '#f9fafb'
                    : base.color,
                }),
                multiValue: base => ({
                  ...base,
                  backgroundColor: document.documentElement.classList.contains('dark')
                    ? '#374151'
                    : base.backgroundColor,
                }),
                multiValueLabel: base => ({
                  ...base,
                  color: document.documentElement.classList.contains('dark')
                    ? '#f9fafb'
                    : base.color,
                }),
                multiValueRemove: base => ({
                  ...base,
                  color: document.documentElement.classList.contains('dark')
                    ? '#9ca3af'
                    : base.color,
                  '&:hover': {
                    backgroundColor: document.documentElement.classList.contains('dark')
                      ? '#4b5563'
                      : '#e5e7eb',
                    color: document.documentElement.classList.contains('dark')
                      ? '#f9fafb'
                      : '#374151',
                  },
                }),
                input: base => ({
                  ...base,
                  color: document.documentElement.classList.contains('dark')
                    ? '#f9fafb'
                    : base.color,
                }),
              }}
              filterOption={(option, inputValue) => {
                // Don't show already selected tags in dropdown
                if (selectedTags.includes(option.value)) {
                  return false;
                }
                return option.label.toLowerCase().includes(inputValue.toLowerCase());
              }}
            />
            <p className="text-xs text-muted-foreground">
              {getTagsModalLabels.labels.searchOrCreate}
            </p>
          </div>

          {/* Changes Summary */}
          {hasChanges && (
            <div className="rounded-md border border-primary/30 bg-primary/10 p-3 dark:bg-primary/20">
              <div className="flex items-center gap-2 text-primary dark:text-primary">
                <Plus className="size-4" />
                <span className="text-sm font-medium">
                  {getProductComponentsText('productTagsModal.changes.detected')}
                </span>
              </div>
              <p className="mt-1 text-xs text-primary/80 dark:text-primary/80">
                {selectedTags.length} tags will be saved to this product
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleCancel} disabled={isSubmitting}>
            {getTagsModalLabels.buttons.cancel}
          </Button>
          <Button
            onClick={handleSave}
            disabled={!hasChanges || isSubmitting}
            className="min-w-[100px]"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 size-4 animate-spin" />
                {getTagsModalLabels.buttons.saving}
              </>
            ) : (
              <>
                <TagIcon className="mr-2 size-4" />
                {getTagsModalLabels.buttons.save}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ProductTagsModal;
